import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import DateInputBR from "@/components/DateInputBR";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileText, Plus, Search, Printer, CheckCircle2, AlertTriangle,
  TrendingUp, User, Building2, Calendar, Trash2, History,
} from "lucide-react";
import { toast } from "sonner";
import { PageHero, KpiCard, Section } from "@/components/AnalyticsCommon";
import PromotionLetterDocument from "./PromotionLetterDocument";

const fmtBRL = (n) => (n == null ? "—" : Number(n).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
};

export default function PromotionLettersPage() {
  const { companyParam, companies } = useCompany();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLetter, setShowLetter] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      const { data } = await api.get("/promotion-letters", { params });
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyParam]);

  const totals = useMemo(() => ({
    total: list.length,
    applied: list.filter((l) => l.applied_to_employee).length,
    pending: list.filter((l) => !l.applied_to_employee).length,
    avg_increase: list.length
      ? Math.round(list.reduce((acc, l) => acc + (l.diff_pct || 0), 0) / list.length * 10) / 10
      : 0,
  }), [list]);

  return (
    <div className="space-y-5 pb-10" data-testid="promotion-letters-page">
      <PageHero
        kicker={<><FileText className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Pessoas</>}
        title="Cartas de Promoção"
        description="Emita cartas formais de promoção com auto-preenchimento, aplique a promoção ao cadastro com 1 clique e mantenha histórico completo de auditoria."
        actions={<NewLetterDialog onCreated={(letter) => { load(); setShowLetter(letter); }} />}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pl-kpis">
        <KpiCard label="Cartas emitidas" value={totals.total} icon={FileText} accent="emerald" testId="pl-total" />
        <KpiCard label="Aplicadas" value={totals.applied}
                sub={`${totals.pending} pendentes`} icon={CheckCircle2} accent="ochre" testId="pl-applied" />
        <KpiCard label="Aumento médio" value={`${totals.avg_increase}%`}
                sub="Salário" icon={TrendingUp} accent="sky" testId="pl-increase" />
        <KpiCard label="Empresas no escopo" value={companies.length}
                icon={Building2} accent="slate" testId="pl-companies" />
      </div>

      <Section kicker="Histórico" title="Cartas emitidas" testId="pl-history">
        {loading ? (
          <div className="grid gap-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={`sk-${i}`} className="h-[100px]" />)}
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-10 text-[13px] text-muted-foreground">
            <History className="h-8 w-8 mx-auto text-[#C5A059] mb-2" />
            Nenhuma carta de promoção emitida ainda. Clique em <b>Nova carta</b> para começar.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {list.map((l) => (
              <LetterCard key={l.id} letter={l}
                          onView={() => setShowLetter(l)}
                          onChange={load} />
            ))}
          </div>
        )}
      </Section>

      {showLetter && <PromotionLetterDocument letter={showLetter}
                                          onClose={() => setShowLetter(null)}
                                          onApplied={load} />}
    </div>
  );
}

function LetterCard({ letter, onView, onChange }) {
  const remove = async () => {
    if (!window.confirm("Excluir esta carta? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete(`/promotion-letters/${letter.id}`);
      toast.success("Carta excluída.");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const diffCls = letter.diff_pct > 20 ? "text-emerald-700"
                : letter.diff_pct > 0 ? "text-[#8A6B2B]" : "text-slate-600";
  return (
    <div className="rounded-xl border border-border bg-white p-4 hover:shadow-md transition-shadow"
         data-testid={`pl-card-${letter.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <User className="h-3 w-3 text-[#C5A059]" />
            <span className="font-display text-[15px] font-semibold truncate">{letter.employee_name}</span>
          </div>
          <div className="text-[10.5px] text-muted-foreground">
            Mat. {letter.matricula} · {letter.company_nome_fantasia}
          </div>
        </div>
        {letter.applied_to_employee
          ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Aplicada</Badge>
          : <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">Não aplicada</Badge>}
      </div>
      <div className="rounded-md bg-slate-50 border border-border p-2.5 my-2">
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <div>
            <div className="text-muted-foreground uppercase tracking-wider text-[9px]">De</div>
            <div className="truncate">{letter.cargo_anterior || "—"}</div>
            <div className="font-mono tabular-nums text-slate-500">{fmtBRL(letter.salario_anterior)}</div>
          </div>
          <div>
            <div className="text-[#8A6B2B] uppercase tracking-wider text-[9px] font-semibold">Para</div>
            <div className="truncate font-medium">{letter.new_cargo}</div>
            <div className="font-mono tabular-nums font-semibold text-[#234935]">{fmtBRL(letter.new_salario)}</div>
          </div>
        </div>
        {letter.diff_pct != null && (
          <div className={`mt-1.5 text-[10.5px] flex items-center gap-1 ${diffCls}`}>
            <TrendingUp className="h-3 w-3" />
            <b>+{letter.diff_pct}%</b> de aumento
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground mb-2">
        <Calendar className="h-3 w-3" />
        Efetiva em {fmtDate(letter.data_efetiva)} · Emitida em {fmtDate(letter.issued_at)} por {letter.issued_by_name}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={onView} data-testid={`pl-view-${letter.id}`}>
          <Printer className="h-3 w-3 mr-1" /> Ver / Imprimir
        </Button>
        {!letter.applied_to_employee && (
          <Button size="sm" variant="ghost" onClick={remove}
                  className="text-rose-600 hover:bg-rose-50 ml-auto"
                  data-testid={`pl-del-${letter.id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

// =================================================================
// New letter dialog
// =================================================================
function NewLetterDialog({ onCreated }) {
  const { companyParam } = useCompany();
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    new_cargo: "", new_salario: "", data_efetiva: new Date().toISOString().slice(0, 10),
    motivo: "Promoção por mérito e desempenho", observacoes: "", saudacao_responsavel: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingEmp(true);
    // Respeita empresa selecionada. Se nenhuma empresa (Grupo), pega todos os colaboradores.
    // page_size alto para trazer todos os ativos do grupo/empresa (backend não tem cap).
    const params = { page_size: 5000, status: "ativo" };
    if (companyParam) params.company_id = companyParam;
    api.get("/employees", { params })
      .then(({ data }) => setEmployees(data.items || []))
      .finally(() => setLoadingEmp(false));
  }, [open, companyParam]);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    if (!q) return employees;
    return employees.filter((e) =>
      (e.nome || "").toLowerCase().includes(q) ||
      (e.matricula || "").toLowerCase().includes(q) ||
      (e.cargo || "").toLowerCase().includes(q),
    );
  }, [employees, search]);

  const pickEmployee = async (emp) => {
    setSelected(emp);
    try {
      const { data } = await api.get(`/promotion-letters/employee/${emp.id}/preview`);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const reset = () => {
    setSelected(null); setPreview(null); setSearch("");
    setForm({
      new_cargo: "", new_salario: "", data_efetiva: new Date().toISOString().slice(0, 10),
      motivo: "Promoção por mérito e desempenho", observacoes: "", saudacao_responsavel: "",
    });
  };

  const save = async () => {
    if (!selected) { toast.error("Selecione um colaborador"); return; }
    if (!form.new_cargo) { toast.error("Informe o novo cargo"); return; }
    if (!form.new_salario || Number(form.new_salario) <= 0) { toast.error("Informe o novo salário"); return; }
    setSaving(true);
    try {
      const payload = {
        employee_id: selected.id,
        new_cargo: form.new_cargo,
        new_salario: Number(form.new_salario),
        data_efetiva: form.data_efetiva,
        motivo: form.motivo || null,
        observacoes: form.observacoes || null,
        saudacao_responsavel: form.saudacao_responsavel || null,
      };
      const { data } = await api.post("/promotion-letters", payload);
      toast.success("Carta de promoção emitida.");
      setOpen(false);
      reset();
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const diffPct = useMemo(() => {
    const old = preview?.employee?.salario;
    const next = Number(form.new_salario);
    if (!old || !next) return null;
    return Math.round(((next - old) / old) * 1000) / 10;
  }, [preview, form.new_salario]);

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-10 bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"
                data-testid="pl-new-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Nova carta
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto"
                     aria-describedby="pl-new-desc" data-testid="pl-new-dialog">
        <DialogHeader>
          <DialogTitle>Nova carta de promoção</DialogTitle>
        </DialogHeader>
        <p id="pl-new-desc" className="text-[12px] text-muted-foreground">
          Selecione o colaborador para preenchimento automático e informe os novos dados.
        </p>

        {/* Step 1: Pick employee */}
        {!selected ? (
          <div className="space-y-3 mt-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Buscar por nome, matrícula ou cargo..."
                     className="pl-9 h-10" autoFocus
                     data-testid="pl-emp-search" />
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground px-1">
              <span data-testid="pl-emp-count">
                {loadingEmp ? "Carregando colaboradores…" : `${filtered.length} de ${employees.length} colaboradores`}
              </span>
              {companyParam ? (
                <span>Empresa selecionada</span>
              ) : (
                <span>Todas as empresas (Grupo)</span>
              )}
            </div>
            <div className="border border-border rounded-md max-h-[380px] overflow-y-auto">
              {loadingEmp ? (
                <div className="p-4 space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={`sk-${i}`} className="h-12" />)}
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-6 text-center text-[12.5px] text-muted-foreground">Nenhum colaborador encontrado.</div>
              ) : (
                filtered.map((emp) => (
                  <button key={emp.id} type="button" onClick={() => pickEmployee(emp)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-border last:border-0 transition-colors"
                          data-testid={`pl-emp-${emp.id}`}>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-[#234935] text-white grid place-items-center text-[11px] font-semibold shrink-0">
                        {(emp.nome || "?").charAt(0)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium truncate">{emp.nome}</div>
                        <div className="text-[10.5px] text-muted-foreground truncate">
                          Mat. {emp.matricula} · {emp.cargo || "—"} · {fmtBRL(emp.salario)}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          // Step 2: Form
          <div className="space-y-3 mt-2">
            <div className="rounded-lg border border-[#234935]/20 bg-[#234935]/5 p-3">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-[#234935] text-white grid place-items-center font-semibold shrink-0">
                  {(selected.nome || "?").charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-[14px]">{selected.nome}</div>
                  <div className="text-[10.5px] text-muted-foreground">
                    Mat. {selected.matricula} · {preview?.company?.nome_fantasia} · {preview?.employee?.sector_name}
                  </div>
                  <div className="text-[10.5px] text-muted-foreground">
                    Cargo atual: <b>{preview?.employee?.cargo || "—"}</b> · Salário atual: <b>{fmtBRL(preview?.employee?.salario)}</b>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { setSelected(null); setPreview(null); }}
                        data-testid="pl-change-emp">
                  Trocar
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Novo cargo *</Label>
                <Input value={form.new_cargo} onChange={(e) => setForm({ ...form, new_cargo: e.target.value })}
                       placeholder="Ex.: Coordenador de Operações"
                       data-testid="pl-new-cargo" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Novo salário (R$) *</Label>
                <Input type="number" step="0.01" value={form.new_salario}
                       onChange={(e) => setForm({ ...form, new_salario: e.target.value })}
                       placeholder="0,00"
                       data-testid="pl-new-salario" />
                {diffPct !== null && (
                  <div className={`text-[10.5px] mt-1 ${diffPct > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {diffPct > 0 ? "+" : ""}{diffPct}% em relação ao atual
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Data efetiva</Label>
                <DateInputBR value={form.data_efetiva}
                       onChange={(v) => setForm({ ...form, data_efetiva: v })}
                       testId="pl-effective" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Responsável (assinatura)</Label>
                <Input value={form.saudacao_responsavel}
                       onChange={(e) => setForm({ ...form, saudacao_responsavel: e.target.value })}
                       placeholder="Ex.: Marcus Vinícius — Diretor de RH"
                       data-testid="pl-signer" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Motivo</Label>
              <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Observações (opcional)</Label>
              <Textarea rows={2} value={form.observacoes}
                        onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                        placeholder="Reconhecimentos, conquistas, expectativas..." />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving || !selected}
                  className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"
                  data-testid="pl-save">
            {saving ? "Emitindo..." : "Emitir carta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =================================================================
// Letter Preview Dialog (printable)
// =================================================================
function LetterPreviewDialog({ letter, onClose, onApplied }) {
  const [applying, setApplying] = useState(false);
  const apply = async () => {
    if (!window.confirm(`Aplicar a promoção ao cadastro de ${letter.employee_name}?\n\nIsto vai atualizar Cargo e Salário no cadastro do colaborador.`)) return;
    setApplying(true);
    try {
      await api.post(`/promotion-letters/${letter.id}/apply`, { update_employee: true });
      toast.success("Promoção aplicada ao cadastro do colaborador.");
      onApplied && onApplied();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setApplying(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[820px] max-h-[92vh] overflow-y-auto print:max-w-none print:max-h-none print:overflow-visible"
                     aria-describedby="lt-desc" data-testid="pl-preview-dialog">
        <DialogHeader className="print:hidden">
          <DialogTitle>Carta de Promoção · {letter.employee_name}</DialogTitle>
        </DialogHeader>
        <p id="lt-desc" className="sr-only">Pré-visualização da carta de promoção pronta para impressão</p>

        {/* Printable letter */}
        <div className="print:p-0" data-testid="letter-document">
          <div className="bg-white rounded-lg border border-border p-8 sm:p-12 print:border-0 print:rounded-none print:p-12">
            {/* Header with logo */}
            <div className="flex items-start justify-between border-b-2 border-[#234935] pb-5 mb-6">
              <div className="flex items-center gap-4">
                {letter.company_logo_url ? (
                  <img src={letter.company_logo_url} alt="logo" className="h-16 w-auto object-contain"
                       data-testid="letter-logo" />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-[#234935] text-[#C5A059] grid place-items-center font-display font-bold text-lg">
                    {(letter.company_nome_fantasia || "??").substring(0, 2).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="font-display text-[18px] font-bold text-[#234935]">
                    {letter.company_nome_fantasia}
                  </div>
                  <div className="text-[11px] text-slate-600">{letter.company_razao_social}</div>
                  {letter.company_cnpj && <div className="text-[10px] text-slate-500">CNPJ {letter.company_cnpj}</div>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A6B2B] font-semibold">Documento Oficial</div>
                <div className="text-[12px] font-medium mt-0.5">Carta de Promoção</div>
                <div className="text-[10px] text-slate-500 mt-0.5 font-mono">N° {letter.id.substring(0, 8).toUpperCase()}</div>
              </div>
            </div>

            <div className="text-right text-[12px] text-slate-700 mb-6">
              {fmtDate(letter.issued_at)}
            </div>

            <h1 className="font-display text-[22px] font-semibold text-[#234935] mb-1">
              {letter.employee_name}
            </h1>
            <div className="text-[11px] text-slate-600 mb-5">
              Matrícula {letter.matricula} · {letter.sector_name || "—"}
            </div>

            <p className="text-[13px] leading-relaxed text-slate-800 mb-3">
              É com grande satisfação que comunicamos sua <b>promoção</b> dentro do quadro funcional da <b>{letter.company_nome_fantasia}</b>,
              em reconhecimento ao seu desempenho, dedicação e contribuição contínua aos resultados da empresa.
            </p>

            <p className="text-[13px] leading-relaxed text-slate-800 mb-5">
              A partir de <b>{fmtDate(letter.data_efetiva)}</b>, você passa a exercer a função de:
            </p>

            <div className="grid grid-cols-2 gap-4 my-5 print:gap-3">
              <div className="rounded-lg border border-slate-300 bg-slate-50 p-4">
                <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-1">Cargo anterior</div>
                <div className="font-display text-[15px] font-semibold text-slate-700">{letter.cargo_anterior || "—"}</div>
                <div className="text-[12px] text-slate-600 font-mono tabular-nums mt-1">{fmtBRL(letter.salario_anterior)}</div>
              </div>
              <div className="rounded-lg border-2 border-[#C5A059] bg-gradient-to-br from-[#FAF4E5] to-white p-4">
                <div className="text-[9px] uppercase tracking-[0.22em] text-[#8A6B2B] font-semibold mb-1">Novo cargo</div>
                <div className="font-display text-[15px] font-semibold text-[#234935]">{letter.new_cargo}</div>
                <div className="text-[12px] text-[#234935] font-mono tabular-nums font-semibold mt-1">{fmtBRL(letter.new_salario)}</div>
                {letter.diff_pct != null && (
                  <div className="text-[10px] text-[#8A6B2B] mt-1">↑ {letter.diff_pct}% de aumento</div>
                )}
              </div>
            </div>

            {letter.motivo && (
              <p className="text-[13px] leading-relaxed text-slate-800 mb-3">
                <b>Motivo:</b> {letter.motivo}
              </p>
            )}

            {letter.observacoes && (
              <div className="rounded-md bg-slate-50 border border-border p-3 mb-5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Observações</div>
                <p className="text-[12.5px] leading-relaxed text-slate-700 italic">{letter.observacoes}</p>
              </div>
            )}

            <p className="text-[13px] leading-relaxed text-slate-800 mb-2">
              Parabenizamos pela conquista e desejamos sucesso nesta nova etapa profissional.
              Continue contribuindo com seu comprometimento, ética e excelência.
            </p>

            <p className="text-[13px] text-slate-800 mt-6 mb-12">Atenciosamente,</p>

            <div className="border-t border-slate-300 pt-2 max-w-[340px]">
              <div className="text-[12px] font-semibold text-[#234935]">{letter.saudacao_responsavel || letter.issued_by_name}</div>
              <div className="text-[10px] text-slate-500">{letter.company_nome_fantasia}</div>
            </div>

            <div className="border-t border-slate-200 pt-3 mt-10 flex items-center justify-between text-[9px] text-slate-400">
              <span>Documento gerado por TheoKratos RH em {fmtDate(letter.issued_at)}</span>
              <span className="font-mono">{letter.id}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="print:hidden gap-2 flex-wrap">
          {!letter.applied_to_employee && (
            <Button onClick={apply} disabled={applying}
                    className="bg-[#234935] hover:bg-[#2c5c43] text-white"
                    data-testid="pl-apply-btn">
              <CheckCircle2 className={`h-3.5 w-3.5 mr-1.5 ${applying ? "animate-pulse" : ""}`} />
              Aplicar promoção ao cadastro
            </Button>
          )}
          {letter.applied_to_employee && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Promoção já aplicada ao cadastro
            </Badge>
          )}
          <Button onClick={() => window.print()} className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F] ml-auto"
                  data-testid="pl-print-btn">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Imprimir / PDF
          </Button>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>

        <style>{`
          @media print {
            @page { size: A4 portrait; margin: 16mm; }
            body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            aside, nav, header, [data-sidebar] { display: none !important; }
            [role="dialog"] { box-shadow: none !important; border: 0 !important; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
